import { GetProjectMapsSortKey, ProjectMap, SortDirection } from "generated/graphql";
import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import usePaginatedResources from "hooks/usePaginatedResources";
import ProjectMapActionsCell from "modules/projectMaps/ProjectMapActionsCell";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { sortDirectionMap } from "constants/sortDirectionMap";
import MDBox from "components/MDBox";
import { IconButton, InputBase, Paper } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import formatAsMMDDYYYY from "utils/dates/formatAsMMDDYYYY";
import useGetProjectMaps from "./useGetProjectMaps";
import { Cell } from "react-table";

export const projectMapsSortKeyMap: Record<string, GetProjectMapsSortKey> = {
  name: GetProjectMapsSortKey.NAME,
  createdAt: GetProjectMapsSortKey.CREATED,
  updatedAt: GetProjectMapsSortKey.UPDATED,
};

const Search = ({
  defaultValue,
  onQueryChange,
}: {
  defaultValue: string;
  onQueryChange: (v: string) => void;
}) => {
  return (
    <MDBox position="relative" zIndex={2}>
      <MDBox display="flex" alignItems="center" flexWrap="wrap">
        <Paper
          component="form"
          sx={{ p: "2px 4px", display: "flex", alignItems: "center", width: 400, mr: 2 }}
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <InputBase
            autoFocus
            sx={{ ml: 1, flex: 1, fontSize: "14px" }}
            placeholder="Search Maps"
            value={defaultValue}
            inputProps={{ "aria-label": "search maps" }}
            onChange={({ currentTarget }: any) => {
              onQueryChange(currentTarget.value);
            }}
          />
          <IconButton type="button" sx={{ p: "10px" }} aria-label="search">
            <SearchIcon />
          </IconButton>
        </Paper>
      </MDBox>
    </MDBox>
  );
};

export default function useProjectMapsTable() {
  const { filtering, sorting, offset } = usePaginatedVariables({
    initialSearchTerm: null,
  });

  const params = {
    page: offset.page,
    first: offset.first,
    search: filtering.debouncedSearchTerm,
    sortDirection: sortDirectionMap[sorting.sortDirection] || SortDirection.DESC,
    sortKey: projectMapsSortKeyMap[sorting.sortKey] || GetProjectMapsSortKey.UPDATED,
  };

  const { data, loading, error, paginatorInfo, refetch } = useGetProjectMaps(params);

  const pagination = usePaginatedResources({
    paginate: offset.paginate,
    paginatorInfo,
  });

  const searchControl = (
    <Search defaultValue={filtering.searchTerm} onQueryChange={filtering.setSearchTerm} />
  );

  const columns = useMemo(() => {
    return [
      {
        Header: "Name",
        accessor: "name",
        Cell: ({ row, value }: Cell<ProjectMap>) => (
          <Link
            style={{ color: "inherit", display: "block", maxWidth: "300px" }}
            to={`/map/${row.original.id}`}
            title={value}
          >
            {value}
          </Link>
        ),
        width: "300px",
      },
      {
        Header: "Created By",
        accessor: "createdBy",
        disableSortBy: true,
        Cell: ({ value }: Cell<ProjectMap>) =>
          value ? `${value.firstName ?? ""} ${value.lastName ?? ""}`.trim() : "",
      },
      {
        Header: "Date Created",
        accessor: "createdAt",
        Cell: ({ value }: Cell<ProjectMap>) => formatAsMMDDYYYY(value, true),
        width: "160px",
      },
      {
        Header: "Last Updated",
        accessor: "updatedAt",
        Cell: ({ value }: Cell<ProjectMap>) => formatAsMMDDYYYY(value, true),
        width: "160px",
      },
      {
        align: "center",
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ProjectMapActionsCell,
        width: "84px",
      },
    ];
  }, []);

  const tableData = useMemo(() => {
    return { columns, rows: data ?? [] };
  }, [columns, data]);

  return {
    data,
    loading,
    error,
    columns,
    tableData,
    pagination,
    filtering,
    sorting,
    paginatorInfo,
    refetch,
    searchControl,
  } as const;
}
